import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/165188d6/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import '@carbon/charts/style.css';
import { BarChart, PieChart, DonutChart, LineChart, ScatterChart } from '@carbon/charts-react';
import { groupedBarData, groupedBarOptions, simpleBarData, simpleBarOptions, stackedBarData, stackedBarOptions, curvedLineData, curvedLineOptions, lineData, lineOptions, demoPieData, demoPieOptions, demoDonutOptions, scatterData, scatterOptions } from '../../../data/charts/demo-chart-data';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const InlineNotification = makeShortcode("InlineNotification");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const Caption = makeShortcode("Caption");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`Basic charts offer a way to visualize data sets in an intuitive, easy to understand way. Every chart should tell a story and should reflect the content on the page where it is found.`}</p>
    </PageDescription>



    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Bar chart</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Line chart</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Area chart</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Polar charts</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Scatter plots</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Meter and gauge</AnchorLink>
    </AnchorLinks>
    <h2 {...{
      "id": "bar-chart"
    }}>{`Bar chart`}</h2>
    <h3 {...{
      "id": "default"
    }}>{`Default`}</h3>
    <p>{`Bar charts use vertical or horizontal data markers to compare individual values. You can use them to compare discrete data or show trends over time.`}</p>
    <BarChart data={simpleBarData} options={simpleBarOptions} width="100%" height={400} mdxType="BarChart" />
    <h3 {...{
      "id": "grouped-bar-chart"
    }}>{`Grouped bar chart`}</h3>
    <p>{`A grouped bar chart, also known as a clustered bar graph, multi-set bar chart, or grouped column chart, is a type of bar graph that is used to compare values across multiple categories.`}</p>
    <BarChart data={groupedBarData} options={groupedBarOptions} width="100%" height={400} mdxType="BarChart" />
    <h3 {...{
      "id": "stacked-bar"
    }}>{`Stacked bar`}</h3>
    <p>{`Stacked bar charts are useful for comparing proportional contributions within a category. They plot the relative value that each data series contributes to the total.`}</p>
    <BarChart data={stackedBarData} options={stackedBarOptions} width="100%" height={400} mdxType="BarChart" />
    <h3 {...{
      "id": "horizontal-bar"
    }}>{`Horizontal bar`}</h3>
    <p>{`The horizontal bar chart follows the same principles as a vertical column chart, only the x- and y-axis are switched.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` This chart is currently a concept and not included in the Carbon Charts repository yet. For more information, please go to the carbon-charts `}<a parentName="p" {...{
          "href": "https://github.com/carbon-design-system/carbon-charts"
        }}>{`GitHub repository`}</a>{`.`}</p>
    </InlineNotification>
    <Row mdxType="Row">
      <Column colLg={8} colMd={6} colSm={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAABDklEQVQoz51Sa0/EIBDs//9l+kE/qqc16un5SuyLBQoFRh5tpTVnL5JMdrqww06XwjmH9XLWnQ6XEOt8LALhnKOqKtR1DSEZ/rMm0SiotQYjhqbusL9ucbglHO4oxZG/7AjPN4TXkuPtPiHw9weOQdsg+dNhIH0vwVqO8qLF7rxZ4qxBedlif8VAjYYSBj0f4XlufbQsIAQHJ+G7YP5Wgc8ngY/HFMM3q/Sm5VmQiMC9YP3VRWtbRX8hCkopoVQP6gQkDbHYehs5ThGLgtN0lFboWgYlTdYNFk/iGF9YnhLWWv8fJcxg582Qy2NeuN6bYpHfgjT8X4fSK+jBGJvz6zPGmGWHsw13fCCheGt9AwBuXxxCNBpwAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Horizontal bar chart",
            "title": "Horizontal bar chart",
            "src": "/static/cbde589a2ad2ffb02aa7f3b4cfd443ac/fb070/charts-basic-horizontalbar.png",
            "srcSet": ["/static/cbde589a2ad2ffb02aa7f3b4cfd443ac/d6747/charts-basic-horizontalbar.png 288w", "/static/cbde589a2ad2ffb02aa7f3b4cfd443ac/09548/charts-basic-horizontalbar.png 576w", "/static/cbde589a2ad2ffb02aa7f3b4cfd443ac/fb070/charts-basic-horizontalbar.png 1152w", "/static/cbde589a2ad2ffb02aa7f3b4cfd443ac/fb104/charts-basic-horizontalbar.png 1728w", "/static/cbde589a2ad2ffb02aa7f3b4cfd443ac/cf416/charts-basic-horizontalbar.png 1856w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2 {...{
      "id": "line-chart"
    }}>{`Line chart`}</h2>
    <h3 {...{
      "id": "default-1"
    }}>{`Default`}</h3>
    <p>{`Line charts plot data at regular intervals connected by lines. You can use line visualizations to show trends over time and compare several data sets.`}</p>
    <LineChart data={lineData} options={lineOptions} width="100%" height={400} mdxType="LineChart" />
    <h3 {...{
      "id": "stepped-line-chart"
    }}>{`Stepped line chart`}</h3>
    <p>{`Stepped line charts plot data at regular intervals, forming a series of steps between data points. You can use line visualizations to show trends over time and compare several data sets.`}</p>
    <LineChart data={lineData} options={Object.assign({}, lineOptions, {
      curve: 'curveStepAfter'
    })} width="100%" height={400} mdxType="LineChart" />
    <h3 {...{
      "id": "curved-line-chart"
    }}>{`Curved line chart`}</h3>
    <LineChart data={curvedLineData} options={curvedLineOptions} width="100%" height={400} mdxType="LineChart" />
    <h2 {...{
      "id": "area-chart"
    }}>{`Area chart`}</h2>
    <h3 {...{
      "id": "default-2"
    }}>{`Default`}</h3>
    <p>{`Area charts are similar to line charts, but the areas below the lines are filled with colors or patterns. Stacked charts are useful for comparing proportional contributions within a category. They plot the relative value that each data series contributes to the total.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} colMd={6} colSm={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAABu0lEQVQoz5WSXWsTURCG+1/10gsLtZhQLwTvRKiKtSqlqWgCjYgavRARL1TS1qRuUmuihUIblWQ/cppudhM3e/bx7Ec2Fq3BYYeZc2bOO+/OzEwQBPxLwvj/6Ez46Ej06OgGhtXFNE36jpsm/K0A4RdwIucE4HHXxTo6xug52LaN2esx8rxTWUg5BoqR/2AYiu/7U383BJAJI1eB1gwPPzmP7yNAIQSO40SVpZSJxn4EJuNkW3h82RJ8b9q8/tSnoPXZaA0VmbhYyjDsmztwTplKlKpaIGm8F+y+siiVTK4/0ll+onNTaVHddQ7dCUOra9Lt2IiDn1gqoLds2pZgX/9BdX+Pjb0mz15q5PMfyBV3uVpssaSA7j61WClZ3Fg3KNf7E8Dh0KHZaLO6pnFttcKF5TJzK2UuP95k8U2VK8qev/WW2TvvOLtU5sztTc7d22Y+95ns/abKbfCiasSAftKfytc2c2tbZAoamXyNzMMaFx/UubS+o851soUdssouqNhCoaZ8TcW3lf3IbK7C8+q3CcOwmd5olK7DeBjRVH0ZbcBgMFC+jx8OLFmddGi/vUkBp0kQyOk5Sn8BlZo1mPRkz4cAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Horizontal bar chart",
            "title": "Horizontal bar chart",
            "src": "/static/a60223545d1f2c4f1152bc303bd625a6/fb070/charts-basic-area.png",
            "srcSet": ["/static/a60223545d1f2c4f1152bc303bd625a6/d6747/charts-basic-area.png 288w", "/static/a60223545d1f2c4f1152bc303bd625a6/09548/charts-basic-area.png 576w", "/static/a60223545d1f2c4f1152bc303bd625a6/fb070/charts-basic-area.png 1152w", "/static/a60223545d1f2c4f1152bc303bd625a6/fb104/charts-basic-area.png 1728w", "/static/a60223545d1f2c4f1152bc303bd625a6/cf416/charts-basic-area.png 1856w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3 {...{
      "id": "stacked-area-chart"
    }}>{`Stacked area chart`}</h3>
    <p>{`Stacked area charts are useful for comparing proportional contributions within a category. They plot the relative value that each data series contributes to the total.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p>{`Note: This chart is currently a work-in-progress. To see our roadmap, request missing guidance, or contribute content, please go to the carbon-charts `}<a parentName="p" {...{
          "href": "https://github.com/carbon-design-system/carbon-charts"
        }}>{`GitHub repository`}</a>{`.`}</p>
    </InlineNotification>
    <Row mdxType="Row">
      <Column colLg={8} colMd={6} colSm={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAABo0lEQVQoz42SCW/aQBCF+f9/plLjtCgRUqIogA02BWLscKhSoEQBX/iCgO0va3O5aZp2pNmV9s28mZ03lSzL+Mxy/H89t0p+eO6Kl8WSpe1gWRZhFP0W9LcCH4B7Qt+JsT2f5SoiCHwsz2O33X7aSX6naUr6DqscyZMk+aOTcnCefLRXUey9pWnpy67jEsfxh18pv4XrNZ3xmGtNo/04ZLZcMhWjijabU2xBmM8tCIITyXSxYDSbsXDd09tczLcmiC7q91QVmctmA0mWkRp1aqqK7ftlUVwm0ykt00QxBlwoClKrzTeR0DJ0mvoDl417vqsaV/qIarfPdX/CzcCiZj7zVe1hPj2dCddxxPjXnC9NUbGjc/vgcDMKuZq4SL0x0o8BVf0ntaHPnbmm3gvoyD6DuxW9ZsBtN2Bkx3vC43xeFiFy36arBwwVD6PtoZkR6jBGM0K6RsRAWzGUbUayU/ijYjMWbjQc5s+bc4c56W67E1IL8nSvaJarmoh7l5BtE16jmFRsQoEdNiBJ0vMWCKwQJTsQFsQHPylcXoss5Z8mEt4AVMlHqaez7FUAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Horizontal bar chart",
            "title": "Horizontal bar chart",
            "src": "/static/d3f1651990763f57819ede9b541ce844/fb070/charts-basic-areastacked.png",
            "srcSet": ["/static/d3f1651990763f57819ede9b541ce844/d6747/charts-basic-areastacked.png 288w", "/static/d3f1651990763f57819ede9b541ce844/09548/charts-basic-areastacked.png 576w", "/static/d3f1651990763f57819ede9b541ce844/fb070/charts-basic-areastacked.png 1152w", "/static/d3f1651990763f57819ede9b541ce844/fb104/charts-basic-areastacked.png 1728w", "/static/d3f1651990763f57819ede9b541ce844/cf416/charts-basic-areastacked.png 1856w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2 {...{
      "id": "polar-charts"
    }}>{`Polar charts`}</h2>
    <h3 {...{
      "id": "pie"
    }}>{`Pie`}</h3>
    <PieChart data={demoPieData} options={demoPieOptions} width="100%" height={400} mdxType="PieChart" />
    <h3 {...{
      "id": "donut"
    }}>{`Donut`}</h3>
    <DonutChart data={demoPieData} options={demoDonutOptions} width="100%" height={400} mdxType="DonutChart" />
    <h2 {...{
      "id": "scatter-plots"
    }}>{`Scatter plots`}</h2>
    <h3 {...{
      "id": "default-3"
    }}>{`Default`}</h3>
    <p>{`Scatter plot visualizations use data points to plot two measures anywhere along a scale, not only at regular tick marks. You can use scatter plots to explore correlations between different measures.`}</p>
    <ScatterChart data={scatterData} options={scatterOptions} width="100%" height={400} mdxType="ScatterChart" />
    <h3 {...{
      "id": "bubble-chart"
    }}>{`Bubble chart`}</h3>
    <p>{`Bubble charts use data points and bubbles to plot measures anywhere along a scale. One measure is plotted along each axis. The size of the bubble represents the third measure. You can use bubble charts to represent financial data or any data where measured values are related.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} colMd={6} colSm={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "56.25%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAABO0lEQVQoz41T226DMAzt/z/vG/Yj/YI9TNrjpEmdOsSlMJI4cXJmp0mBrusaZJyAfXx8DLuUEv5bGvOI6drpbehHNE2DbhjBMSIwg0JAkv1fBRcgoIbo+QzYGpyGAc1oYRxhNgbdNCEo6B02GaRea4YV/dbisGaZigFeimk3kVM2FNAMOM8zvPewnvBNtqSkC4OYRIbIcIbRSze5K8lRWWLYMs+Ak7QXhcn+9R1Pz3t8NITgJLk36I8W00wYnEPfBXwd7FZL3ABUzbywO3QWL2+dFCC0rcXnyeLYCmsbcrKV1lxM0qqyXtr8BajTZJlsXVHbXKmWn6leMRa9RVsx4rSJWQBloxNV8+QzuGrKHIrn5X05O4kjH2DF13eXKWtlItp6SVRfAcZxlGmeC9Xn1z4DPvKn3PusrmN+ALUTYx7T8jB9AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Horizontal bar chart",
            "title": "Horizontal bar chart",
            "src": "/static/503adb59b01e85d623adcdb5f28bd60e/fb070/charts-basic-bubble.png",
            "srcSet": ["/static/503adb59b01e85d623adcdb5f28bd60e/d6747/charts-basic-bubble.png 288w", "/static/503adb59b01e85d623adcdb5f28bd60e/09548/charts-basic-bubble.png 576w", "/static/503adb59b01e85d623adcdb5f28bd60e/fb070/charts-basic-bubble.png 1152w", "/static/503adb59b01e85d623adcdb5f28bd60e/fb104/charts-basic-bubble.png 1728w", "/static/503adb59b01e85d623adcdb5f28bd60e/cf416/charts-basic-bubble.png 1856w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2 {...{
      "id": "meter-and-gauge"
    }}>{`Meter and gauge`}</h2>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Meter and gauge charts are not yet available in Carbon Charts. These explorations are subject to change.`}</p>
    </InlineNotification>
    <p>{`Meter and gauge charts are useful for showing values between a small number of variables either by using multiple markers on the same meter or gauge or by using multiples of the chart.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "576px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "100%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABXElEQVQ4y61T2U7DMBDs/78jwf/0jYciURBVLyClaQ47ceIcjWUPtgUlQWniFiytVtbao9nZ2Qn++UzaF6XUKf+Odt0ZcOiTC9hZhpxzBEGAJElAKYUQosP8YsCqqpCmKZgOzvNOfUyGXsD6eERME5QauCgr8KJ0lqEXcHcIML2fYbl5tfG+2zsPqgMobU3hgQK3C8AnDHmW2vaNlt9BCLGyOLcsdRZS2qwGYhTQsFNfLF2Os4ZqhNmQ0SfXGvjcu16GjDF4nmeHEUWRHYQxue/79t6IxohjlXEbih5IXdd2Q8w0RdOgLEvkeY4sz1qukG4MsyzDdrsFJTFiom2SpJ1Pa+ZhTpYnR4wCeiHF9HGN2cbH6u0DB73Xhq1pFZrUU7TELHjuZdlr7LkmdKcJ3LwAi7BAzZnWMbGxD0IUTO93I625LvChPOvHNpDCYMs/rRu2f96UNqBSuMrcn0OnKQUEMchbAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Meters",
            "title": "Meters",
            "src": "/static/b4a67b2a9fb9dee3c54756816a4054e9/09548/charts-basic-meter.png",
            "srcSet": ["/static/b4a67b2a9fb9dee3c54756816a4054e9/d6747/charts-basic-meter.png 288w", "/static/b4a67b2a9fb9dee3c54756816a4054e9/09548/charts-basic-meter.png 576w"],
            "sizes": "(max-width: 576px) 100vw, 576px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
Meter charts can represent data with current value, minimum and maximum, and peaks or averages.
        </Caption>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "576px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "100%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABUUlEQVQ4y+VU2U7DMBDs/38KT7xzSFRIhTYEKrXQcrQqFKgQiePmcExqD84FJo6LkHhBrLSaxNrMHrNOB79snb9DKKX8wO9cj99aoS2oadvijAoJpfBIgDCKkDCmMC7e6TpEQNfFmd6JlVAIUeDl9AbdXh9nwxEuRhOMJ1P0nQFOHBc95xzXd3NjND9qOVN5HgMgTjcqQLTG6s9Gy0LkKAvsDiV2jiV2XWD5EoEGPjzfB+fcENJSYYmcK5I9wLmS2FQJasvJPM9DHMetpB2drCY82Admc2kk0i0nbVb6lbAaz4Oa+elRGcDYG0hAQcMQScLw6pNiC+pKCSGG4prKJbqHwGpRE3KMlerLp5XyZ8wW98X61OareWZZZmm5IrwdAAn9FIixFGnKkfLS9Y8jtatWwuasbLehTVnL2uji2O9127lV5ababUW2EVtb/h8/2HeGSCjiwJcl2QAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Gauge",
            "title": "Gauge",
            "src": "/static/f70f8aace897d77c6ab428db4aa2ac21/09548/charts-basic-gauge.png",
            "srcSet": ["/static/f70f8aace897d77c6ab428db4aa2ac21/d6747/charts-basic-gauge.png 288w", "/static/f70f8aace897d77c6ab428db4aa2ac21/09548/charts-basic-gauge.png 576w"],
            "sizes": "(max-width: 576px) 100vw, 576px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  Gauge animates to the current value when dashboard loads to create emphasis.
        </Caption>
      </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      